import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { Flame, Droplets, Zap, Cable, HardHat, Wrench, ShieldCheck, Truck } from 'lucide-react';

const services = [
  {
    icon: Flame,
    title: 'Gas',
    description: 'Aanleg, onderhoud en reparatie van gasleidingen en -installaties conform de strengste veiligheidsnormen en regelgeving.',
  },
  {
    icon: Droplets,
    title: 'Water',
    description: 'Professionele aanleg en onderhoud van waterleidingen, rioleringen en drinkwaterinfrastructuur.',
  },
  {
    icon: Zap,
    title: 'Elektra',
    description: 'Installatie en beheer van elektriciteitsnetwerken, van laagspanning tot middenspanning en aansluitingen.',
  },
  {
    icon: Cable,
    title: 'Kabeltracés',
    description: 'Professionele aanleg van ondergrondse kabeltracés voor energie- en telecommunicatienetwerken.',
  },
  {
    icon: HardHat,
    title: 'Civiele Werken',
    description: 'Grondwerk, bestrating en civiele constructies ter ondersteuning van infrastructuurprojecten.',
  },
  {
    icon: Wrench,
    title: 'Onderhoud & Beheer',
    description: 'Preventief en correctief onderhoud van gas-, water- en elektranetwerken en infrastructurele voorzieningen.',
  },
  {
    icon: ShieldCheck,
    title: 'Veiligheidsdiensten',
    description: 'Veiligheidscoördinatie, LMRA-begeleiding en toezicht op naleving van veiligheidsnormen.',
  },
  {
    icon: Truck,
    title: 'Materieel & Logistiek',
    description: 'Levering van gespecialiseerd materieel en logistieke ondersteuning voor grootschalige projecten.',
  },
];

export default function Services() {
  const [ref, inView] = useInView({ threshold: 0.1 });

  return (
    <section id="services" ref={ref} className="section-padding relative overflow-hidden bg-ferry-darker">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-ferry-crimson/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-ferry-crimson/30 to-transparent" />

      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-ferry-crimson/5 rounded-full blur-[200px]" />

      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-ferry-crimson font-semibold text-sm uppercase tracking-widest">Onze Diensten</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6">
            Wat wij <span className="gradient-text">bieden</span>
          </h2>
          <p className="text-ferry-gray-text max-w-2xl mx-auto leading-relaxed">
            Van gas en water tot elektra — Ferry Infra levert een compleet pakket 
            aan infrastructuurdiensten met focus op veiligheid, kwaliteit en efficiëntie.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
              className="glass-card p-8 group hover:bg-white/10 hover:border-ferry-crimson/20 transition-all duration-500 hover:-translate-y-1"
            >
              <div className="w-14 h-14 rounded-2xl bg-ferry-crimson/10 flex items-center justify-center mb-6 group-hover:bg-ferry-crimson/20 group-hover:scale-110 transition-all duration-500">
                <service.icon size={26} className="text-ferry-crimson" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
              <p className="text-ferry-gray-text leading-relaxed text-sm">{service.description}</p>
              <div className="mt-6 w-0 group-hover:w-12 h-0.5 bg-ferry-crimson transition-all duration-500" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
